import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, DollarSign, Percent, Users, Award } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DiversityKPIs } from '@/hooks/useDiversityAnalytics';

interface DiversityKPICardsProps {
  data: DiversityKPIs | undefined;
  isLoading: boolean;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

function TrendIndicator({ 
  current, 
  previous, 
  isPercentage = false,
  higherIsBetter = true 
}: { 
  current: number; 
  previous: number;
  isPercentage?: boolean;
  higherIsBetter?: boolean;
}) {
  const diff = current - previous;
  const isPositive = higherIsBetter ? diff > 0 : diff < 0;
  const isNeutral = Math.abs(diff) < 0.01;

  if (isNeutral) {
    return <span className="text-xs text-muted-foreground">No change</span>;
  }

  const Icon = isPositive ? TrendingUp : TrendingDown;
  const formattedDiff = isPercentage 
    ? `${diff > 0 ? '+' : ''}${diff.toFixed(1)}pp`
    : `${diff > 0 ? '+' : ''}${formatPercentage(Math.abs(previous) > 0 ? ((current - previous) / previous) * 100 : 0)}`;

  return (
    <span className={cn(
      'flex items-center gap-1 text-xs font-medium',
      isPositive ? 'text-emerald-600' : 'text-red-600'
    )}>
      <Icon className="h-3 w-3" />
      {formattedDiff} vs prior period
    </span>
  );
}

function KPICard({
  title,
  icon: Icon,
  primaryValue,
  supportingText,
  trend,
  isLoading,
  iconClassName,
}: {
  title: string;
  icon: React.ElementType;
  primaryValue: string;
  supportingText: string;
  trend: React.ReactNode;
  isLoading: boolean;
  iconClassName?: string;
}) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-28" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold tracking-tight">{primaryValue}</p>
            <p className="text-xs text-muted-foreground">{supportingText}</p>
          </div>
          <div className={cn('rounded-full p-2', iconClassName || 'bg-primary/10')}>
            <Icon className={cn('h-5 w-5', iconClassName ? 'text-white' : 'text-primary')} />
          </div>
        </div>
        <div className="mt-3">
          {trend}
        </div>
      </CardContent>
    </Card>
  );
}

export function DiversityKPICards({ data, isLoading }: DiversityKPICardsProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <KPICard
        title="Diversity Spend"
        icon={DollarSign}
        primaryValue={data ? formatCurrency(data.diverseSpendCents) : '$0'}
        supportingText={data ? `${data.diverseJobCount} jobs awarded to diverse vendors` : '0 jobs'}
        trend={
          data ? (
            <TrendIndicator 
              current={data.diverseSpendCents} 
              previous={data.prevDiverseSpendCents}
            />
          ) : null
        }
        isLoading={isLoading}
        iconClassName="bg-emerald-500"
      />

      <KPICard
        title="Diversity Spend %"
        icon={Percent}
        primaryValue={data ? formatPercentage(data.diverseSpendPercentage) : '0%'}
        supportingText={data ? `Of ${formatCurrency(data.totalSpendCents)} total spend` : 'Of $0 total'}
        trend={
          data ? (
            <TrendIndicator 
              current={data.diverseSpendPercentage} 
              previous={data.prevDiversePercentage}
              isPercentage
            />
          ) : null
        }
        isLoading={isLoading}
        iconClassName="bg-blue-500"
      />

      <KPICard
        title="Diverse Vendors Engaged"
        icon={Users}
        primaryValue={data ? data.diverseVendorCount.toString() : '0'}
        supportingText={data ? `${formatPercentage(data.diverseVendorPercentage)} of all vendors used` : '0% of all vendors'}
        trend={
          data ? (
            <TrendIndicator 
              current={data.diverseVendorCount} 
              previous={data.prevDiverseVendorCount}
            />
          ) : null
        }
        isLoading={isLoading}
        iconClassName="bg-violet-500"
      />

      <KPICard
        title="Diverse Vendor Win Rate"
        icon={Award}
        primaryValue={data ? formatPercentage(data.diverseWinRate) : '0%'}
        supportingText={data ? `${data.diverseJobsAwarded} awarded / ${data.diverseBidsSubmitted} bids` : '0 awarded / 0 bids'}
        trend={
          data ? (
            <TrendIndicator 
              current={data.diverseWinRate} 
              previous={data.prevWinRate}
              isPercentage
            />
          ) : null
        }
        isLoading={isLoading}
        iconClassName="bg-amber-500"
      />
    </div>
  );
}
